import { Modal } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useResponsive } from '../../hooks/use_responsive';
import { WebAnalytics } from '../../lib/web_analytics';
import { isOpen, ProUpsellModalActions } from '../../redux/pro_upsell_modal';
import { MembershipSelector } from '../../routes/pro_page/membership_selector';
import { ProComparisonChart } from '../../routes/pro_page/pro_comparison_chart';
import { ProTrialButton } from '../pro_buttons/pro_trial_button';
import styles from './pro_upsell_modal.less';
import { ReviewsCarousel } from './reviews_carousel';

export const ProUpsellModal = () => {
  const open = useSelector(isOpen);
  const dispatch = useDispatch();
  const { isMobile } = useResponsive();
  const [selectedTier, setSelectedTier] = React.useState<'core' | 'pro'>('pro');
  const tierProSelected = selectedTier === 'pro';
  const tierCoreSelected = selectedTier === 'core';

  return (
    <Modal
      open={open}
      onCancel={() => dispatch(ProUpsellModalActions.PRO_UPSELL_MODAL_CLOSE())}
      footer={
        <div className={'flex flex-col items-end gap-1'}>
          <ProTrialButton
            size={isMobile ? 'md' : 'xl'}
            kind="primary"
            onClick={() => {
              WebAnalytics.proButtonClicked({
                location: 'pro_upsell_modal',
                type: 'trial',
                style: 'button',
              });
            }}
            core={tierCoreSelected}
            text={`Try Omnia ${tierProSelected ? 'PRO' : 'Core'} for FREE`}
          />
          <p className="text-[#6B7172]">7-Day Free Trial. Cancel before Day 7, no charge.</p>
        </div>
      }
      width={'575px'}
      wrapClassName={styles.wrapper}
    >
      <div className="px-4 sm:px-8 pb-6 pt-4 flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col">
            <p className="text-[#FF2A00] font-termina font-bold">GUEST PASS</p>
            <p className="font-termina text-2xl font-bold leading-none border-b-[#FF2A00] border-b-[3px] pb-2">
              Unlock the Best Maps & Rewards in Fishing for Free
            </p>
          </div>
          <p className="text-base leading-snug">
            Trusted by thousands of anglers. <br />
            Try FREE for 7 days.
          </p>
        </div>
        <div>
          <ReviewsCarousel />
        </div>
        <div>
          <MembershipSelector selectedTier={selectedTier} onTierChange={setSelectedTier} />
        </div>

        <div>
          <h3 className="font-termina font-bold mb-3">
            Try Omnia {tierProSelected ? 'PRO' : 'Core'} for FREE
          </h3>
          <ol
            className={classNames(
              "flex flex-col gap-2.5 relative before:contents-[''] before:absolute before:left-2 before:w-1  before:h-full ",
              {
                'before:bg-[#CFECF6]': tierCoreSelected,
                'before:bg-[#E8DBAD]': tierProSelected,
              }
            )}
          >
            <li className="flex gap-2.5 relative">
              <span
                className={classNames(
                  'flex justify-center items-center rounded-full w-[21px] h-[21px]',
                  {
                    'bg-[#CFECF6]': tierCoreSelected,
                    'bg-[#E8DBAD]': tierProSelected,
                  }
                )}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M12.0001 6.25189H4.18756V2.75189C4.18756 2.26908 4.57975 1.87689 5.06256 1.87689H8.93756C9.42037 1.87689 9.81256 2.26908 9.81256 2.75189V3.81439C9.81256 3.88314 9.86881 3.93939 9.93756 3.93939H10.8126C10.8813 3.93939 10.9376 3.88314 10.9376 3.81439V2.75189C10.9376 1.6472 10.0422 0.751892 8.93756 0.751892H5.06256C3.95787 0.751892 3.06256 1.6472 3.06256 2.75189V6.25189H2.00006C1.7235 6.25189 1.50006 6.47533 1.50006 6.75189V12.7519C1.50006 13.0285 1.7235 13.2519 2.00006 13.2519H12.0001C12.2766 13.2519 12.5001 13.0285 12.5001 12.7519V6.75189C12.5001 6.47533 12.2766 6.25189 12.0001 6.25189ZM11.3751 12.1269H2.62506V7.37689H11.3751V12.1269ZM6.56256 9.95502V10.7831C6.56256 10.8519 6.61881 10.9081 6.68756 10.9081H7.31256C7.38131 10.9081 7.43756 10.8519 7.43756 10.7831V9.95502C7.56654 9.86242 7.66281 9.73128 7.71251 9.58048C7.76222 9.42968 7.76279 9.267 7.71415 9.11585C7.66551 8.96471 7.57017 8.83289 7.44185 8.73938C7.31352 8.64588 7.15884 8.5955 7.00006 8.5955C6.84128 8.5955 6.6866 8.64588 6.55827 8.73938C6.42995 8.83289 6.33461 8.96471 6.28597 9.11585C6.23733 9.267 6.2379 9.42968 6.28761 9.58048C6.33731 9.73128 6.43358 9.86242 6.56256 9.95502Z"
                    fill="#061622"
                  />
                </svg>
              </span>
              Today: Start your free 7-day trial
            </li>
            <li className="flex gap-2.5 relative">
              <span
                className={classNames(
                  'flex justify-center items-center rounded-full w-[21px] h-[21px]',
                  {
                    'bg-[#CFECF6]': tierCoreSelected,
                    'bg-[#E8DBAD]': tierProSelected,
                  }
                )}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M11.75 10.9981H11.375V5.68561C11.375 3.48092 9.74531 1.65905 7.625 1.35592V0.748108C7.625 0.402795 7.34531 0.123108 7 0.123108C6.65469 0.123108 6.375 0.402795 6.375 0.748108V1.35592C4.25469 1.65905 2.625 3.48092 2.625 5.68561V10.9981H2.25C1.97344 10.9981 1.75 11.2215 1.75 11.4981V11.9981C1.75 12.0669 1.80625 12.1231 1.875 12.1231H5.25C5.25 13.0887 6.03438 13.8731 7 13.8731C7.96563 13.8731 8.75 13.0887 8.75 12.1231H12.125C12.1938 12.1231 12.25 12.0669 12.25 11.9981V11.4981C12.25 11.2215 12.0266 10.9981 11.75 10.9981ZM7 12.8731C6.58594 12.8731 6.25 12.5372 6.25 12.1231H7.75C7.75 12.5372 7.41406 12.8731 7 12.8731ZM3.75 10.9981V5.68561C3.75 4.81686 4.0875 4.00123 4.70156 3.38717C5.31563 2.77311 6.13125 2.43561 7 2.43561C7.86875 2.43561 8.68438 2.77311 9.29844 3.38717C9.9125 4.00123 10.25 4.81686 10.25 5.68561V10.9981H3.75Z"
                    fill="#061622"
                  />
                </svg>
              </span>
              Day 5: Get a trial reminder
            </li>
            <li className="flex gap-2.5 relative">
              <span
                className={classNames(
                  'flex justify-center items-center rounded-full w-[21px] h-[21px]',
                  {
                    'bg-[#CFECF6]': tierCoreSelected,
                    'bg-[#E8DBAD]': tierProSelected,
                  }
                )}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M13.1892 4.5183L9.22196 3.94174L7.44853 0.346429C7.40009 0.247992 7.3204 0.168304 7.22196 0.119867C6.97509 -0.00200837 6.67509 0.0995541 6.55165 0.346429L4.77821 3.94174L0.811026 4.5183C0.701651 4.53393 0.601651 4.58549 0.525088 4.66362C0.432529 4.75875 0.381524 4.88674 0.383282 5.01946C0.38504 5.15218 0.439416 5.27878 0.534463 5.37143L3.40478 8.16987L2.72665 12.1214C2.71075 12.2134 2.72092 12.3079 2.75601 12.3943C2.79111 12.4808 2.84972 12.5556 2.9252 12.6105C3.00068 12.6653 3.09001 12.6978 3.18306 12.7045C3.27611 12.7111 3.36916 12.6915 3.45165 12.648L7.00009 10.7824L10.5485 12.648C10.6454 12.6996 10.7579 12.7167 10.8657 12.698C11.1376 12.6511 11.3204 12.3933 11.2735 12.1214L10.5954 8.16987L13.4657 5.37143C13.5438 5.29487 13.5954 5.19487 13.611 5.08549C13.6532 4.81205 13.4626 4.55893 13.1892 4.5183ZM9.38759 7.77612L9.95165 11.0621L7.00009 9.51205L4.04853 11.0636L4.61259 7.77768L2.22509 5.44955L5.52509 4.96987L7.00009 1.9808L8.47509 4.96987L11.7751 5.44955L9.38759 7.77612Z"
                    fill="#061622"
                  />
                </svg>
              </span>
              Day 7: You’ll be charged ${tierProSelected ? 59 : 39}/year
            </li>
          </ol>
        </div>
        <ProComparisonChart selectedTier={selectedTier} />
      </div>
    </Modal>
  );
};
